import { Extension } from "@tiptap/core";
import { Decoration, DecorationSet } from "prosemirror-view";
import { Plugin, PluginKey } from "prosemirror-state";
import type { Node as ProseMirrorNode } from "prosemirror-model";

export interface FactCheckDecorationRange {
  from: number;
  to: number;
  severity?: string;
  findingId?: number;
  isAccepted?: boolean;
}

const factCheckDecorationsKey = new PluginKey<DecorationSet>("factCheckDecorations");

const buildDecorations = (
  doc: ProseMirrorNode,
  ranges: FactCheckDecorationRange[]
): DecorationSet => {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return DecorationSet.empty;
  }

  const decorations = ranges
    .filter((range) => Number.isFinite(range.from) && Number.isFinite(range.to) && range.from < range.to)
    .map((range) => {
      const acceptedClass = range.isAccepted ? " fact-check-accepted" : "";
      return Decoration.inline(range.from, range.to, {
        class: `fact-check-underline fact-check-${range.severity || "warning"}${acceptedClass}`,
        "data-fact-check-id": String(range.findingId ?? ""),
      });
    });

  return DecorationSet.create(doc, decorations);
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    factCheckDecorations: {
      setFactCheckRanges: (ranges: FactCheckDecorationRange[]) => ReturnType;
      clearFactCheckRanges: () => ReturnType;
    };
  }
}

export const FactCheckDecorations = Extension.create({
  name: "factCheckDecorations",

  addCommands() {
    return {
      setFactCheckRanges:
        (ranges: FactCheckDecorationRange[]) =>
        ({ state, dispatch }) => {
          if (!dispatch) return true;
          dispatch(state.tr.setMeta(factCheckDecorationsKey, { setRanges: ranges || [] }));
          return true;
        },
      clearFactCheckRanges:
        () =>
        ({ state, dispatch }) => {
          if (!dispatch) return true;
          dispatch(state.tr.setMeta(factCheckDecorationsKey, { clear: true }));
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: factCheckDecorationsKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old) {
            const meta = tr.getMeta(factCheckDecorationsKey);

            if (meta?.clear) {
              return DecorationSet.empty;
            }

            if (meta?.setRanges) {
              return buildDecorations(tr.doc, meta.setRanges);
            }

            return old.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return factCheckDecorationsKey.getState(state);
          },
        },
      }),
    ];
  },
});
